import { AddCompaniesPage } from "@/frontend/components/AddCompaniesPage/ui/AddCompaniesPage";
import { CompaniesPage } from "@/frontend/pages/CompaniesPage";
import { DashboardPage } from "@/frontend/pages/DashboardPage/ui/DashboardPage";
import { SignInPage } from "@/frontend/pages/SignInPage/ui/SignInPage";
import {
  AppRoutes,
  getRouteAddCompany,
  getRouteCompanies,
  getRouteDashboard,
  getRouteSignIn,
  getRouteForgotPassword,
  getRouteResetPassword,
  getRouteSignUp,
  getRouteSettingsPage,
  getRoutePortfolioCompanyProfile,
  getRouteCompanyProfile,
  getRouteTermsOfUse,
  getRoutePrivacyNotice,
  getRouteFounderCompanyProfile,
  getRouteFounderSignUp,
  getRouteExpertRootPage,
  getRouteExpertSignUp,
  getRouteExpertOrganizationById,
  getRouteExpertPortfolioCompanyProfile,
  getRouteExpertCompaniesById,
  getRouteExpertCompanyProfile,
  getRouteArchivedCompanies,
  getRouteDeutscheTelekom,
  getRouteApplicationFormSignUp
} from "@/frontend/shared/const/router";
import { ResetPasswordPage } from "@/frontend/pages/ResetPasswordPage";
import { ForgotPasswordPage } from "@/frontend/pages/ForgotPasswordPage";
import { Navigate, RouteProps } from "react-router-dom";
import { SignUpPage } from "@/frontend/pages/SignUpPage";
import { PortfolioCompanyProfilePage } from "@/frontend/pages/PortfolioCompanyProfilePage/ui/PortfolioCompanyProfilePage";
import { SettingsPage } from "@/frontend/pages/SettingsPage/ui/SettingsPage";
import { PrivacyNoticePage } from "@/frontend/pages/PrivacyNoticePage/PrivacyNoticePage";
import { TermsOfUsePage } from "@/frontend/pages/TermsOfUsePage/TermsOfUsePage";
import { CompanyProfilePage } from "@/frontend/pages/CompanyProfilePage";
import { FounderCompanyProfilePage } from "@/frontend/pages/FounderCompanyProfilePage/ui/FounderCompanyProfilePage";
import { FounderSignUpPage } from "@/frontend/pages/FounderSignUpPage";
import { ExpertRootPage } from "@/frontend/pages/ExpertRootPage/ui/ExpertRootPage";
import { ArchivedCompaniesPage } from "@/frontend/pages/ArchivedCompaniesPage";
import { ExpertPortfolioCompanyProfilePage } from "@/frontend/pages/ExpertPortfolioCompanyProfilePage";
import { ApplicationFormDeutscheTelekom } from "@/frontend/pages/ApplicationFormDeutscheTelekom";
import { ApplicationFormSignUp } from "@/frontend/pages/ApplicationFormSignUp";

export enum ERoleAccess {
  FOUNDER = "Founder",
  INVESTOR = "Investor",
  NONE = "",
  ALL = "All",
  EXPERT = "Expert"
}

type TRouteAccess = "protected" | "nonprotected" | "public";

export type AppRoutesProps = RouteProps & {
  routePrivilege: TRouteAccess;
  roleAccess?: ERoleAccess;
};

export const routeConfig: Record<AppRoutes, AppRoutesProps> = {
  [AppRoutes.WRONG_PAGE]: {
    path: "*",
    element: <Navigate to={getRouteSignIn()} replace />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.MAIN]: {
    path: "/",
    element: <Navigate to={getRouteDashboard()} />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.INVESTOR
  },
  [AppRoutes.DASHBOARD]: {
    path: getRouteDashboard(),
    element: <DashboardPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.INVESTOR
  },
  [AppRoutes.ARCHIVED_COMPANIES]: {
    path: getRouteArchivedCompanies(),
    element: <ArchivedCompaniesPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.INVESTOR
  },
  [AppRoutes.EXPERT_ROOT_PAGE]: {
    path: getRouteExpertRootPage(),
    element: <ExpertRootPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.EXPERT
  },
  [AppRoutes.COMPANIES]: {
    path: getRouteCompanies(),
    element: <CompaniesPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.ALL
  },
  [AppRoutes.ADD_COMPANY]: {
    path: getRouteAddCompany(),
    element: <AddCompaniesPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.INVESTOR
  },
  [AppRoutes.PORTFOLIO_COMPANY_PROFILE]: {
    path: getRoutePortfolioCompanyProfile(":id"),
    element: <PortfolioCompanyProfilePage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.INVESTOR
  },
  [AppRoutes.EXPERT_COMPANY_PROFILE]: {
    path: getRouteExpertCompanyProfile(":organizationId", ":id"),
    element: <CompanyProfilePage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.ALL
  },
  [AppRoutes.EXPERT_PORTFOLIO_COMPANY_PROFILE]: {
    path: getRouteExpertPortfolioCompanyProfile(":organizationId", ":id"),
    element: <ExpertPortfolioCompanyProfilePage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.EXPERT
  },
  [AppRoutes.FOUNDER_COMPANY_PROFILE]: {
    path: getRouteFounderCompanyProfile(),
    element: <FounderCompanyProfilePage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.FOUNDER
  },
  [AppRoutes.COMPANY_PROFILE]: {
    path: getRouteCompanyProfile(":id"),
    element: <CompanyProfilePage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.ALL
  },
  [AppRoutes.SETTINGS]: {
    path: getRouteSettingsPage(),
    element: <SettingsPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.INVESTOR
  },
  [AppRoutes.PRIVACY_NOTICE]: {
    path: getRoutePrivacyNotice(),
    element: <PrivacyNoticePage />,
    routePrivilege: "public"
  },
  [AppRoutes.TERMS_OF_USE]: {
    path: getRouteTermsOfUse(),
    element: <TermsOfUsePage />,
    routePrivilege: "public"
  },
  [AppRoutes.SIGNIN]: {
    path: getRouteSignIn(),
    element: <SignInPage />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.RESET_PASSWORD]: {
    path: getRouteResetPassword(),
    element: <ResetPasswordPage />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.FORGOT_PASSWORD]: {
    path: getRouteForgotPassword(),
    element: <ForgotPasswordPage />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.SIGNUP]: {
    path: getRouteSignUp(),
    element: <SignUpPage />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.FOUNDER_SIGN_UP]: {
    path: getRouteFounderSignUp(),
    element: <FounderSignUpPage />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.EXPERT_SIGN_UP]: {
    path: getRouteExpertSignUp(),
    element: <FounderSignUpPage />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.EXPERT_ORGANIZATION_BY_ID]: {
    path: getRouteExpertOrganizationById(":organizationId"),
    element: <ExpertRootPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.EXPERT
  },
  [AppRoutes.EXPERT_COMPANIES_BY_ID]: {
    path: getRouteExpertCompaniesById(":organizationId"),
    element: <CompaniesPage />,
    routePrivilege: "protected",
    roleAccess: ERoleAccess.EXPERT
  },
  [AppRoutes.DEUTSCHE_TELEKOM]: {
    path: getRouteDeutscheTelekom(),
    element: <ApplicationFormDeutscheTelekom />,
    routePrivilege: "nonprotected"
  },
  [AppRoutes.APPLICATION_FORM_SIGN_UP]: {
    path: getRouteApplicationFormSignUp(),
    element: <ApplicationFormSignUp />,
    routePrivilege: "nonprotected"
  }
};
